import React, { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    CardHeader,
    Checkbox,
    FormControl,
    IconButton,
    InputLabel,
    Link,
    MenuItem,
    Select,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import { Close as RemoveIcon } from '@mui/icons-material';
import Grid from '@mui/material/Grid2';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs, { Dayjs } from 'dayjs';

import { translate as t } from 'hooks/useTranslations';
import CommonLayout from 'views/App/Portal/_layouts/CommonLayout';
import useIssuedManual from 'views/App/Portal/Policy/IssuedManuals/_data_sources/useIssuedManual';
import useIssuedManualGroups, {
    ManualGroup,
} from 'views/App/Portal/Policy/IssuedManuals/_data_sources/useIssuedManualGroups';
import { QuickSearch } from 'components/_atoms';
import { ValidationErrors } from '../_types/errors';
import TruncatedText from '_lex/_atoms/TruncatedText';

export enum ScheduleFrequency {
    Once = 'ONCE',
    Monthly = 'MONTH',
    Quarterly = 'QUARTERYEAR',
    SemiAnnually = 'HALFYEAR',
    Annually = 'YEAR',
}

export interface AcknowledgementScheduleFormProps {
    title: string;
    manualId: string;
    mode: 'new' | 'edit';
    onCancelHref: string;
    onSubmit: (payload: {
        startAt: string;
        policies: { id: string; }[];
        groups: { id: string; }[];
        dueDays: number;
        frequency: string;
        title: string;
    }) => void;
    isSaving?: boolean;
    externalErrors?: ValidationErrors;
    initialValues?: {
        title?: string;
        startAt?: Dayjs;
        frequency?: string;
        dueDays?: number;
        groups?: ManualGroup[];
        policies?: string[]; // policy ids
    };
}

const AcknowledgementScheduleForm: React.FC<AcknowledgementScheduleFormProps> = ({
    title,
    manualId,
    mode,
    onCancelHref,
    onSubmit,
    isSaving = false,
    externalErrors,
    initialValues,
}) => {
    const manualQuery = useIssuedManual(manualId || '');

    const [groupSearch, setGroupSearch] = useState<string>('');
    const manualGroupsQuery = useIssuedManualGroups(
        manualId || '0',
        { search: groupSearch },
        { enabled: !!manualId }
    );

    const [currentTab, setCurrentTab] = useState<string>('settings');
    const [scheduleTitle, setScheduleTitle] = useState<string>(initialValues?.title ?? '');
    const [startAt, setStartAt] = useState<Dayjs>(initialValues?.startAt ?? dayjs().add(1, 'day'));
    const [frequency, setFrequency] = useState<string>(initialValues?.frequency ?? ScheduleFrequency.Monthly);
    const [dueDays, setDueDays] = useState<number>(initialValues?.dueDays ?? 30);
    const [selectedGroups, setSelectedGroups] = useState<ManualGroup[]>(initialValues?.groups ?? []);
    const [selectedPolicies, setSelectedPolicies] = useState<string[]>(initialValues?.policies ?? []);
    const [validationErrors, setValidationErrors] = useState<ValidationErrors>({});

    const initializedFromProps = useRef<boolean>(false);

    // Initialize form state once when initialValues arrive (primarily for edit mode)
    useEffect(() => {
        if (!initializedFromProps.current && initialValues) {
            setScheduleTitle(initialValues.title ?? '');
            setStartAt(initialValues.startAt ?? dayjs().add(1, 'day'));
            setFrequency(initialValues.frequency ?? ScheduleFrequency.Monthly);
            setDueDays(initialValues.dueDays ?? 30);
            setSelectedGroups(initialValues.groups ?? []);
            setSelectedPolicies(initialValues.policies ?? []);
            initializedFromProps.current = true;
        }
    }, [initialValues]);

    // Apply external backend validation errors if provided
    useEffect(() => {
        if (externalErrors && Object.keys(externalErrors).length > 0) {
            setValidationErrors(externalErrors);
            // Switch to tab with first error for better UX
            if (externalErrors.title || externalErrors.startAt || externalErrors.dueDays || externalErrors.frequency) {
                setCurrentTab('settings');
            } else if (externalErrors.policies) {
                setCurrentTab('content');
            } else if (externalErrors.groups) {
                setCurrentTab('groups');
            }
        }
    }, [externalErrors]);

    const [groupRef, groupInView] = useInView();

    const addPolicies = (policies: string[]) => {
        setSelectedPolicies((prev) => {
            return Array.from(new Set([...prev, ...policies])) as string[];
        });
    };

    React.useEffect(() => {
        if (groupInView) {
            manualGroupsQuery.fetchNextPage();
        }
    }, [groupInView, manualGroupsQuery]);

    const groupsTrigger = manualGroupsQuery.hasNextPage ? (
        <Stack
            key='groups-trigger'
            ref={groupRef}
            direction='row'
            spacing={4}
        >
            <Button
                onClick={() => manualGroupsQuery.fetchNextPage()}
                disabled={!manualGroupsQuery.hasNextPage || manualGroupsQuery.isFetchingNextPage}
            >
                {manualGroupsQuery.hasNextPage ? t('common.loadMore') : ''}
            </Button>
        </Stack>
    ) : null;

    const validateForm = (): ValidationErrors => {
        const errors: ValidationErrors = {};

        if (!scheduleTitle || scheduleTitle.trim().length === 0) {
            errors.title = t('policy.acknowledgementSchedules.validation.titleRequired');
        } else if (scheduleTitle.length > 60) {
            errors.title = t('policy.acknowledgementSchedules.validation.titleExceedsMax');
        }

        if (!startAt) {
            errors.startAt = t('policy.acknowledgementSchedules.validation.startAtRequired');
        } else {
            const today = dayjs().startOf('day');
            const selectedDate = dayjs(startAt).startOf('day');
            if (!selectedDate.isAfter(today)) {
                errors.startAt = t('policy.acknowledgementSchedules.validation.startAtMustBeFuture');
            }
        }

        if (!dueDays || dueDays < 1) {
            errors.dueDays = t('policy.acknowledgementSchedules.validation.dueDaysInvalid');
        }

        if (selectedGroups.length === 0) {
            errors.groups = t('policy.acknowledgementSchedules.validation.groupsRequired');
        }

        if (selectedPolicies.length === 0) {
            errors.policies = t('policy.acknowledgementSchedules.validation.policiesRequired');
        }

        if (!frequency) {
            errors.frequency = t('policy.acknowledgementSchedules.validation.frequencyRequired');
        }

        return errors;
    };

    useEffect(() => {
        if (scheduleTitle || startAt || dueDays !== 30 || selectedGroups.length || selectedPolicies.length || frequency) {
            const errors = validateForm();
            setValidationErrors(errors);
        }
    }, [scheduleTitle, startAt, dueDays, selectedGroups.length, selectedPolicies.length, frequency]);

    const manualGroups = useMemo(() => {
        let result =
            manualGroupsQuery.data && manualGroupsQuery.data.pages
                ? manualGroupsQuery.data.pages.flatMap((x) => x.content)
                : [];
        return result;
    }, [manualGroupsQuery.data]);

    const handleSaveClick = () => {
        const errors = validateForm();

        if (Object.keys(errors).length > 0) {
            setValidationErrors(errors);
            if (errors.title || errors.startAt || errors.dueDays || errors.frequency) {
                setCurrentTab('settings');
            } else if (errors.policies) {
                setCurrentTab('content');
            } else if (errors.groups) {
                setCurrentTab('groups');
            }
            return;
        }

        onSubmit({
            startAt: startAt.toISOString().split('T')[0],
            policies: selectedPolicies.map((x) => ({ id: x })),
            groups: selectedGroups.map((x) => ({ id: x.id })),
            dueDays: dueDays,
            frequency: frequency,
            title: scheduleTitle,
        });
    };

    const handleTitleChange = (value: string) => {
        setScheduleTitle(value);
        if (validationErrors.title) {
            setValidationErrors((prev) => ({ ...prev, title: undefined }));
        }
    };

    const disableSave = () => {
        if (isSaving) return true;
        const errors = validateForm();
        const hasErrors = Object.keys(errors).length > 0;
        return hasErrors;
    };

    const getSaveButtonTooltip = () => {
        const errors = validateForm();
        const errorMessages = Object.values(errors).filter(Boolean);
        if (errorMessages.length > 0) {
            return errorMessages.join('. ');
        }
        return '';
    };

    return (
        <CommonLayout
            headerProps={{
                title: title,
                breadcrumbItems: [
                    <Link
                        key='b1'
                        href='..'
                    >
                        {t('policy.issuedManuals.title')}
                    </Link>,
                    <Link
                        key='b2'
                        href={`../${manualId}`}
                    >
                        {manualQuery.data?.name}
                    </Link>,
                    <Typography
                        key='b5'
                        component='span'
                    >
                        {title}
                    </Typography>,
                ],
                actions: (
                    <Stack
                        direction='row'
                        spacing={3}
                    >
                        <Button
                            variant='text'
                            href={onCancelHref}
                        >
                            {t('common.cancel')}
                        </Button>
                        <Button
                            variant='contained'
                            onClick={handleSaveClick}
                            disabled={disableSave()}
                            title={getSaveButtonTooltip()}
                        >
                            {t('common.save')}
                        </Button>
                    </Stack>
                ),
            }}
            tabs={{
                items: [
                    {
                        label: t('policy.acknowledgementSchedules.new.tabs.settings'),
                        value: 'settings',
                    },
                    {
                        label: t('policy.acknowledgementSchedules.new.tabs.content'),
                        value: 'content',
                        count: selectedPolicies.length,
                    },
                    {
                        label: t('policy.acknowledgementSchedules.new.tabs.groups'),
                        value: 'groups',
                        count: selectedGroups.length,
                    },
                ],
                value: currentTab,
                onChange: (event: React.SyntheticEvent, newValue: string | number) => setCurrentTab(newValue.toString()),
            }}
        >
            {currentTab === 'settings' && (
                <Card>
                    <CardContent>
                        <Grid
                            container
                            columnSpacing={4}
                            rowGap={6}
                        >
                            <Grid
                                size={{ xs: 6, sm: 6 }}
                            >
                                <TextField
                                    id='title'
                                    key='title'
                                    label={t('policy.acknowledgementSchedules.new.fields.title')}
                                    error={!!validationErrors.title}
                                    helperText={validationErrors.title}
                                    variant='outlined'
                                    fullWidth
                                    required
                                    value={scheduleTitle}
                                    onChange={({ target }) => handleTitleChange(target.value)}
                                    inputProps={{
                                        maxLength: 60,
                                    }}
                                />
                            </Grid>
                            <Grid
                                size={{ xs: 6, sm: 6 }}
                            ></Grid>
                            <Grid
                                size={{ xs: 3, sm: 3 }}
                            >
                                <DatePicker
                                    label={t('policy.acknowledgementSchedules.new.fields.startAt')}
                                    value={startAt}
                                    disablePast
                                    onChange={(newDate) => {
                                        setStartAt(newDate ?? dayjs());
                                        if (validationErrors.startAt) {
                                            setValidationErrors(prev => ({ ...prev, startAt: undefined }));
                                        }
                                    }}
                                    slotProps={{
                                        textField: {
                                            fullWidth: true,
                                            error: !!validationErrors.startAt,
                                            helperText: validationErrors.startAt,
                                        },
                                    }}
                                />
                            </Grid>
                            <Grid
                                size={{ xs: 9, sm: 9 }}
                            ></Grid>

                            <Grid
                                size={{ xs: 3, sm: 3 }}
                            >
                                <FormControl
                                    key='frequency'
                                    fullWidth
                                >
                                    <InputLabel
                                        id='frequency-label'
                                        required
                                    >
                                        {t('policy.acknowledgementSchedules.new.fields.recurrence')}
                                    </InputLabel>
                                    <Select
                                        id='frequency'
                                        label={t('policy.acknowledgementSchedules.new.fields.recurrence')}
                                        fullWidth
                                        value={frequency}
                                        onChange={({ target }) => {
                                            setFrequency(target.value);
                                        }}
                                    >
                                        {Object.entries(ScheduleFrequency).map(([k, v]) => (
                                            <MenuItem
                                                key={`frequency_${v}`}
                                                value={v}
                                            >
                                                {t(`policy.acknowledgementSchedules.frequency.${v}`)}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid
                                size={{ xs: 9, sm: 9 }}
                            ></Grid>
                            <Grid
                                size={{ xs: 6, sm: 6 }}
                            >
                                <TextField
                                    id='due_days'
                                    key='due_days'
                                    label={t('policy.acknowledgementSchedules.new.fields.dueDays')}
                                    variant='outlined'
                                    value={dueDays.toString()}
                                    onChange={({ target }) => {
                                        const value = Number(target.value) > 0 ? Number(target.value) : 0;
                                        setDueDays(value);
                                        if (validationErrors.dueDays) {
                                            setValidationErrors(prev => ({ ...prev, dueDays: undefined }));
                                        }
                                    }}
                                    error={!!validationErrors.dueDays}
                                    helperText={validationErrors.dueDays}
                                    inputProps={{
                                        pattern: '[0-9]*',
                                        maxLength: 2,
                                    }}
                                />
                            </Grid>
                            <Grid
                                size={{ xs: 6, sm: 6 }}
                            ></Grid>
                            <Grid
                                size={{ xs: 12, sm: 12 }}
                            >
                                { }
                            </Grid>
                        </Grid>
                    </CardContent>
                </Card>
            )}
            {currentTab === 'content' && (
                <Card>
                    {
                        selectedPolicies.length === 0 && (
                            <Alert severity='warning' sx={{ mb: 2 }}>
                                {t('policy.acknowledgementSchedules.validation.policiesRequired')}
                            </Alert>
                        )
                    }
                    <CardContent>
                        <Stack spacing={4}>
                            {(manualQuery.data?.chapters ?? []).map((chapter) => (
                                <Stack
                                    spacing={2}
                                    key={`chapter_${chapter.id}`}
                                >
                                    <Stack
                                        direction='row'
                                        spacing={2}
                                        sx={{ display: 'flex', alignItems: 'center' }}
                                    >
                                        <Typography sx={{ fontWeight: 600 }}>{[chapter.key, chapter.title].join(': ')}</Typography>
                                        <Button
                                            size='small'
                                            variant='outlined'
                                            color='neutral'
                                            onClick={() => {
                                                addPolicies((chapter.policies ?? []).map((x) => x.id) ?? []);
                                            }}
                                        >
                                            {t('policy.acknowledgementSchedules.selectAll')}
                                        </Button>
                                    </Stack>
                                    {chapter.policies?.map((policy) => (
                                        <Stack
                                            direction='row'
                                            spacing={2}
                                            key={`policies_${policy.id}`}
                                        >
                                            <Checkbox
                                                checked={selectedPolicies.filter((x) => x === policy.id).length > 0}
                                                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                                                    if (event.target.checked) {
                                                        addPolicies([policy.id]);
                                                    } else {
                                                        setSelectedPolicies((prev) => prev.filter((x) => x !== policy.id));
                                                    }
                                                }}
                                                value={policy.id}
                                                name='selected_policies'
                                                sx={{
                                                    p: 0,
                                                }}
                                            />
                                            <Typography>{[policy.key, policy.title].join(': ')}</Typography>
                                        </Stack>
                                    ))}
                                </Stack>
                            ))}
                        </Stack>
                    </CardContent>
                </Card>
            )}
            {currentTab === 'groups' && (
                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: '1fr 1fr',
                        gap: 4,
                    }}
                >
                    <Card>
                        {
                            selectedGroups.length === 0 && (
                                <Alert severity='warning' sx={{ mb: 2 }}>
                                    {t('policy.acknowledgementSchedules.validation.groupsRequired')}
                                </Alert>
                            )
                        }
                        <CardHeader
                            title={'Groups With Access'}
                            subheader={
                                <QuickSearch
                                    id='groups_search'
                                    initialValue={groupSearch}
                                    onChange={(val) => setGroupSearch(val)}
                                    placeholder={t('policy.acknowledgements.groups.searchPlaceholder')}
                                />
                            }
                        />
                        <CardContent
                            sx={{
                                maxHeight: '50vh',
                                overflow: 'auto',
                            }}
                        >
                            <Box
                                sx={{
                                    display: 'grid',
                                    gridTemplateColumns: 'auto 1fr',
                                    gap: 2,
                                    alignItems: 'center',
                                }}
                            >
                                <Fragment key={`groups_all`}>
                                    <Checkbox
                                        checked={selectedGroups.length === manualGroups.length}
                                        onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                                            if (event.target.checked) {
                                                setSelectedGroups(manualGroups);
                                            } else {
                                                setSelectedGroups([]);
                                            }
                                        }}
                                        value={[]}
                                        name='selected_groups'
                                        sx={{
                                            p: 0,
                                        }}
                                    />
                                    <Stack>
                                        <Typography sx={{ fontWeight: 600 }}>All groups</Typography>
                                    </Stack>
                                </Fragment>
                                {manualGroups.map((group) => (
                                    <Fragment key={`groups_${group.id}`}>
                                        <Checkbox
                                            checked={selectedGroups.filter((x) => x.id === group.id).length > 0}
                                            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                                                if (event.target.checked) {
                                                    setSelectedGroups((prev) => [...prev, group]);
                                                } else {
                                                    setSelectedGroups((prev) => prev.filter((x) => x.id !== group.id));
                                                }
                                            }}
                                            value={group.id}
                                            name='selected_groups'
                                            sx={{
                                                p: 0,
                                            }}
                                        />
                                        <Stack>
                                            <TruncatedText
                                                text={group.name}
                                                maxWidth={260}
                                                typographyProps={{ sx: { fontWeight: 600 } }}
                                            />
                                            <Typography variant='caption'>{group.description}</Typography>
                                        </Stack>
                                    </Fragment>
                                ))}
                                {groupsTrigger}
                            </Box>
                        </CardContent>
                    </Card>
                    <Card>
                        <CardHeader title={'Selected Groups'} />
                        <CardContent
                            sx={{
                                maxHeight: '50vh',
                                overflow: 'auto',
                            }}
                        >
                            {selectedGroups.length > 0 ? (
                                <Box
                                    sx={{
                                        display: 'grid',
                                        gridTemplateColumns: 'auto 1fr',
                                        gap: 2,
                                        alignItems: 'center',
                                    }}
                                >
                                    {selectedGroups.map((group) => (
                                        <Fragment key={`selected_groups_${group.id}`}>
                                            <IconButton
                                                onClick={() => setSelectedGroups((prev) => prev.filter((x) => x.id !== group.id))}
                                            >
                                                <RemoveIcon />
                                            </IconButton>
                                            <Stack>
                                                <TruncatedText
                                                    text={group.name}
                                                    maxWidth={260}
                                                    typographyProps={{ sx: { fontWeight: 600 } }}
                                                />
                                                <Typography variant='caption'>{group.description}</Typography>
                                            </Stack>
                                        </Fragment>
                                    ))}
                                </Box>
                            ) : (
                                <Box
                                    sx={{
                                        minHeight: '40vh',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                    }}
                                >
                                    <Stack
                                        sx={{
                                            textAlign: 'center',
                                        }}
                                    >
                                        <img
                                            src='/admin/img/policy/acknowledgements/empty_groups.png'
                                            alt={t('policy.acknowledgements.groups.empty.title')}
                                        />
                                        <Typography variant='subtitle1'>{t('policy.acknowledgements.groups.empty.title')}</Typography>
                                        <Typography variant='body2'>{t('policy.acknowledgements.groups.empty.subtitle')}</Typography>
                                    </Stack>
                                </Box>
                            )}
                        </CardContent>
                    </Card>
                </Box>
            )}
        </CommonLayout>
    );
};

export default AcknowledgementScheduleForm;


